import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { AdminSpecialDeal } from '../types/models/SpecialDeal';
import type { ApiResponseAdminSpecialList } from '../types/api/ApiResponseSpecialDeal';

interface UseAdminSpecialsReturn {
  specials: AdminSpecialDeal[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /admin/billing/specials?client_id={id} — staff-only: ALL of one client's deals (active,
// deactivated and spent alike), newest first, in the full Deal Object shape (internal note,
// billed_event_count). The admin routes authorize on the real staff user and take explicit ids —
// `clientId` is the billed organization (the impersonation target when staff acts for a tenant).
//
// `enabled` (default true) defers the fetch so non-staff consumers never fire the
// backend-rejected admin call; a null `clientId` (no in-scope organization) resolves to an empty
// list without a request.
export const useAdminSpecials = (clientId: number | null, enabled = true): UseAdminSpecialsReturn => {
  const [specials, setSpecials] = useState<AdminSpecialDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchSpecials = useCallback(async () => {
    if (clientId === null) {
      setSpecials([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/billing/specials?client_id=${clientId}`);
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminSpecialList | null;
      if (body?.status === 'success' && body.data?.specials) {
        setSpecials(body.data.specials);
      } else {
        setError(body?.data?.client_id ?? body?.message ?? 'Failed to load special deals');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching special deals');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, clientId]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchSpecials();
    }
  }, [enabled, auth.isAuthenticated, fetchSpecials]);

  return { specials, loading, error, refetch: fetchSpecials };
};
